import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { foundingLine, foundingValidUntil, type FoundingOffer } from "./foundingPrice";

/**
 * Founding price for Playbook + Close Skills: $79 instead of $99 for the first 50 buyers, until
 * 2026-11-21 23:59 PT. Stripe enforces the cap and the end date (coupon p8LqMvXA, promotion code FOUNDING79,
 * see supabase/functions/_shared/founding-offer.ts).
 *
 * The site never decides the offer is on by itself: it asks create-playbook-checkout { action: "status" }, which
 * reads the promotion code and coupon from Stripe. Only an explicit "active" answer shows $79; a failed call, a
 * missing function, an unexpected reply or a passed end date all show $99. A tab left open past the end date flips
 * back to $99 on the next render that reads the status.
 */

export const FOUNDING_BUNDLE_SLUG = "close-playbook-skills";
export const FOUNDING_FN = "create-playbook-checkout";

export type { FoundingOffer } from "./foundingPrice";
export { foundingLine, foundingValidUntil, offerWithFounding } from "./foundingPrice";

/** The reply, accepted only when it says active with sane numbers and an end still ahead. */
export function parseFounding(data: unknown, now = Date.now()): FoundingOffer | null {
  const f = (data as { founding?: Record<string, unknown> } | null)?.founding;
  if (!f || f.active !== true) return null;
  const p = f.price_cents, l = f.list_cents, e = f.ends_at;
  if (typeof p !== "number" || typeof l !== "number" || typeof e !== "string") return null;
  if (!(p > 0 && p < l) || !(Date.parse(e) > now)) return null;
  return { price_cents: p, list_cents: l, ends_at: e };
}

let pending: Promise<FoundingOffer | null> | null = null;

/** One status call per page load, shared by every component that asks. */
export function loadFoundingOffer(): Promise<FoundingOffer | null> {
  if (!pending) {
    pending = supabase.functions.invoke(FOUNDING_FN, { body: { action: "status" } })
      .then(({ data, error }) => (error ? null : parseFounding(data)))
      .catch(() => null);
  }
  return pending.then((o) => (o && Date.parse(o.ends_at) > Date.now() ? o : null));
}

/** The live founding offer, or null (full price). Pass enabled=false for any other product: no call is made. */
export function useFoundingOffer(enabled = true): FoundingOffer | null {
  const [offer, setOffer] = useState<FoundingOffer | null>(null);
  useEffect(() => {
    if (!enabled) { setOffer(null); return; }
    let live = true;
    loadFoundingOffer().then((o) => { if (live) setOffer(o); });
    return () => { live = false; };
  }, [enabled]);
  return offer;
}

/** The price a bundle card shows: for Playbook + Close Skills, the founding price while Stripe says it is live. */
export function useBundlePrice(b: { slug: string; price_cents: number } | null | undefined): {
  cents: number; wasCents: number | null; line: string | null;
  /** PT end date (YYYY-MM-DD) while the founding price shows, for JSON-LD priceValidUntil; null at full price. */
  validUntil: string | null;
} {
  const offer = useFoundingOffer(b?.slug === FOUNDING_BUNDLE_SLUG);
  if (!b) return { cents: 0, wasCents: null, line: null, validUntil: null };
  if (!offer) return { cents: b.price_cents, wasCents: null, line: null, validUntil: null };
  return { cents: offer.price_cents, wasCents: offer.list_cents, line: foundingLine(offer), validUntil: foundingValidUntil(offer) };
}
